/**
 * Unified multi-collection section: category facet (multi-select), search, and sort.
 * URL: ?category=artisan-sandwiches or ?categories=starters,platters
 *
 * Liquid can only print 50 products per collection on a page template.
 * hydrateCatalog() loads each collection's ?view=mcu-ajax feed (up to 250
 * per request) and keeps unique products up to data-mcu-cap.
 */
(() => {
  const ROOT_SEL = '[data-multi-collection-unified-root]';

  /** @param {Node} node */
  function adoptNode(node) {
    if (!(node instanceof Element)) return node;
    return node.ownerDocument === document ? node : document.importNode(node, true);
  }

  /**
   * @param {string} handle
   * @param {string} collectionsBase
   * @param {string} view
   * @returns {Promise<HTMLElement[]>}
   */
  async function fetchCollectionCells(handle, collectionsBase, view) {
    /** @type {HTMLElement[]} */
    const cells = [];
    let page = 1;

    while (page && page < 20) {
      const url = `${collectionsBase}/${encodeURIComponent(handle)}?view=${encodeURIComponent(view)}&page=${page}`;
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) break;

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const ajaxRoot = doc.querySelector('[data-mcu-ajax-root]');
      if (!(ajaxRoot instanceof HTMLElement)) break;

      ajaxRoot.querySelectorAll('[data-mcu-product]').forEach((el) => {
        if (el instanceof HTMLElement) cells.push(adoptNode(el));
      });

      const next = ajaxRoot.querySelector('[data-mcu-ajax-next]');
      page =
        next instanceof HTMLElement && next.dataset.mcuAjaxNext
          ? parseInt(next.dataset.mcuAjaxNext, 10)
          : 0;
    }

    return cells;
  }

  /**
   * Append products missing from the Liquid first paint. Never replace the
   * grid with a smaller set — ajax feeds can return fewer items than Liquid
   * when the alternate template is missing or paginated differently.
   *
   * @param {HTMLElement} root
   * @param {HTMLElement} grid
   * @returns {Promise<number>} number of products appended
   */
  async function hydrateCatalog(root, grid) {
    const sourcesEl = root.querySelector('[data-mcu-sources]');
    if (!(sourcesEl instanceof HTMLScriptElement)) return 0;

    /** @type {{ handle: string, title: string, products_count: number }[]} */
    let sources = [];
    try {
      sources = JSON.parse(sourcesEl.textContent || '[]');
    } catch (err) {
      return 0;
    }
    if (!Array.isArray(sources) || !sources.length) return 0;

    const cap = parseInt(root.dataset.mcuCap || '200', 10) || 200;
    const collectionsBase = root.dataset.mcuCollectionsBase || '/collections';
    const view = root.dataset.mcuAjaxView || 'mcu-ajax';

    /** @type {Set<string>} */
    const existingIds = new Set();
    grid.querySelectorAll('[data-mcu-product]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.productId) {
        existingIds.add(el.dataset.productId);
      }
    });

    /** @type {Record<string, number>} */
    const renderedByHandle = {};
    grid.querySelectorAll('[data-mcu-product]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const handle = el.dataset.collectionHandle || '';
      if (!handle) return;
      renderedByHandle[handle] = (renderedByHandle[handle] || 0) + 1;
    });

    const truncated = sources.filter((src) => {
      const handle = src && src.handle;
      if (!handle) return false;
      return (src.products_count || 0) > (renderedByHandle[handle] || 0);
    });

    if (!truncated.length || existingIds.size >= cap) return 0;

    /** @type {Map<string, HTMLElement[]>} */
    const fetchedByHandle = new Map();
    await Promise.all(
      truncated.map(async (src) => {
        try {
          const cells = await fetchCollectionCells(src.handle, collectionsBase, view);
          fetchedByHandle.set(src.handle, cells);
        } catch (err) {
          fetchedByHandle.set(src.handle, []);
        }
      })
    );

    if (![...fetchedByHandle.values()].some((list) => list.length)) return 0;

    let appended = 0;

    for (const src of sources) {
      if (existingIds.size >= cap) break;

      const handle = src.handle;
      if (!handle) continue;

      const fetched = fetchedByHandle.get(handle);
      if (!fetched?.length) continue;

      for (const cell of fetched) {
        if (!(cell instanceof HTMLElement)) continue;
        const id = cell.dataset.productId;
        if (!id || existingIds.has(id)) continue;

        cell.dataset.collectionHandle = handle;
        if (src.title) cell.dataset.collectionTitle = src.title;

        grid.appendChild(cell);
        existingIds.add(id);
        appended += 1;
        if (existingIds.size >= cap) break;
      }
    }

    return appended;
  }

  /** @param {HTMLElement} root */
  async function initRoot(root) {
    const grid = root.querySelector('[data-mcu-grid]');
    if (!(grid instanceof HTMLElement)) return;

    try {
      await hydrateCatalog(root, grid);
    } catch (err) {
      // Keep the Liquid-rendered cards if the ajax feed fails.
    }

    /** @type {HTMLElement[]} */
    const items = [...root.querySelectorAll('[data-mcu-product]')];
    if (!items.length) {
      grid.classList.remove('multi-collection-unified__grid--loading');
      return;
    }

    const sectionId = root.dataset.sectionId || '';

    const searchInput = root.querySelector('[data-mcu-search]');
    const sortSelect = root.querySelector('[data-mcu-sort]');
    const resetBtn = root.querySelector('[data-mcu-reset]');
    const countNodes = [...root.querySelectorAll('[data-mcu-visible-count]')];
    const emptyState = root.querySelector('[data-mcu-empty]');
    const facetMeta = root.querySelector('[data-mcu-facet-meta="collection"]');

    const filtersSelectedOther =
      root.dataset.mcuFiltersSelectedOther || '__N__ selected';

    /** @returns {HTMLInputElement[]} */
    function collectionCheckboxes() {
      return [
        ...root.querySelectorAll(
          '[data-mcu-facet-options="collection"] input[type="checkbox"][data-mcu-filter-collection]'
        )
      ];
    }

    /** @returns {string[]} */
    function selectedCollectionHandles() {
      return collectionCheckboxes()
        .filter((cb) => cb.checked && cb.value)
        .map((cb) => cb.value);
    }

    /** @param {string} handle */
    function titleForHandle(handle) {
      const cb = collectionCheckboxes().find((c) => c.value === handle);
      return cb?.dataset.collectionTitle || handle;
    }

    function updateCollectionFacetMeta() {
      if (!(facetMeta instanceof HTMLElement)) return;

      const selected = selectedCollectionHandles();
      if (selected.length === 0) {
        facetMeta.textContent = '';
        facetMeta.hidden = true;
        return;
      }

      if (selected.length === 1) {
        facetMeta.textContent = titleForHandle(selected[0]);
      } else {
        const count = String(selected.length);
        facetMeta.textContent = filtersSelectedOther
          .replace(/__N__/g, count)
          .replace(/\{\{\s*count\s*\}\}/g, count);
      }
      facetMeta.hidden = false;
    }

    /** @returns {Map<string, string>} handle -> title */
    function collectionMapFromItems() {
      /** @type {Map<string, string>} */
      const map = new Map();
      items.forEach((el) => {
        const handle = el.dataset.collectionHandle || '';
        const title = el.dataset.collectionTitle || '';
        if (handle && title) map.set(handle, title);
      });
      return map;
    }

    /** @param {Map<string, string>} collections */
    function appendCollectionCheckboxes(collections) {
      const ul = root.querySelector('[data-mcu-facet-options="collection"]');
      if (!(ul instanceof HTMLUListElement)) return;

      ul.innerHTML = '';

      const sorted = [...collections.entries()].sort((a, b) =>
        a[1].localeCompare(b[1], undefined, { sensitivity: 'base' })
      );

      sorted.forEach(([handle, title], ix) => {
        const id = `mcu-coll-${sectionId}-${ix + 1}`.replace(/\s+/g, '-');

        const li = document.createElement('li');
        li.className = 'list-menu__item facets__item';

        const label = document.createElement('label');
        label.htmlFor = id;
        label.className = 'facet-checkbox';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = id;
        input.className = 'styled-checkbox';
        input.value = handle;
        input.dataset.collectionTitle = title;
        input.setAttribute('data-mcu-filter-collection', '');

        const text = document.createTextNode(` ${title}`);

        label.appendChild(input);
        label.appendChild(text);
        li.appendChild(label);
        ul.appendChild(li);
      });
    }

    /** @returns {string[]} */
    function readUrlCategories() {
      const params = new URLSearchParams(window.location.search);
      const multi = params.get('categories');
      if (multi)
        return multi
          .split(',')
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
      const single = params.get('category');
      if (single) return [single.trim().toLowerCase()];
      return [];
    }

    /** @param {string[]} handles */
    function syncUrl(handles) {
      const url = new URL(window.location.href);
      url.searchParams.delete('category');
      url.searchParams.delete('categories');
      if (handles.length === 1) {
        url.searchParams.set('category', handles[0]);
      } else if (handles.length > 1) {
        url.searchParams.set('categories', handles.join(','));
      }
      history.replaceState({}, '', url);
    }

    /** @param {string[]} handles */
    function applyCategorySelection(handles, syncHistory) {
      const normalized = handles.map((h) => h.toLowerCase());
      const checkboxes = collectionCheckboxes();
      const known = new Set(checkboxes.map((cb) => cb.value.toLowerCase()));

      checkboxes.forEach((cb) => {
        cb.checked = normalized.includes(cb.value.toLowerCase());
      });

      if (normalized.length && !normalized.some((h) => known.has(h))) {
        const titles = new Map(
          checkboxes.map((cb) => [
            (cb.dataset.collectionTitle || '').toLowerCase(),
            cb
          ])
        );
        normalized.forEach((token) => {
          const byTitle = titles.get(token);
          if (byTitle) byTitle.checked = true;
        });
      }

      if (syncHistory) syncUrl(selectedCollectionHandles());
    }

    appendCollectionCheckboxes(collectionMapFromItems());
    applyCategorySelection(readUrlCategories(), false);

    function applySort() {
      const mode =
        sortSelect instanceof HTMLSelectElement ? sortSelect.value : 'featured';
      if (!mode || mode === 'featured') return;

      const rows = [...items];
      rows.sort((a, b) => {
        const priceA = parseInt(a.dataset.price || '0', 10);
        const priceB = parseInt(b.dataset.price || '0', 10);
        const titleA = (a.dataset.titleNorm || '').toLowerCase();
        const titleB = (b.dataset.titleNorm || '').toLowerCase();
        if (mode === 'price_asc') return priceA - priceB;
        if (mode === 'price_desc') return priceB - priceA;
        if (mode === 'title_asc')
          return titleA.localeCompare(titleB, undefined, {
            sensitivity: 'base'
          });
        return 0;
      });

      const frag = document.createDocumentFragment();
      rows.forEach((row) => frag.appendChild(row));
      grid.appendChild(frag);
    }

    function filterItems() {
      const q =
        searchInput instanceof HTMLInputElement
          ? searchInput.value.trim().toLowerCase()
          : '';
      const selected = selectedCollectionHandles();
      const selectedSet = new Set(selected.map((h) => h.toLowerCase()));

      let visible = 0;

      items.forEach((el) => {
        const handle = (el.dataset.collectionHandle || '').toLowerCase();
        const collOk =
          selectedSet.size === 0 || selectedSet.has(handle);

        const searchOk = !q.length || (el.dataset.titleNorm || '').includes(q);

        const show = collOk && searchOk;
        el.classList.toggle('mcu--hidden', !show);
        if (show) visible += 1;
      });

      if (sortSelect instanceof HTMLSelectElement) applySort();

      updateCollectionFacetMeta();

      if (countNodes.length)
        countNodes.forEach((n) => {
          n.textContent = String(visible);
        });
      if (emptyState) emptyState.classList.toggle('mcu--hidden', visible !== 0);
      grid.classList.remove('multi-collection-unified__grid--loading');
    }

    function debounce(fn, ms) {
      let timer = null;
      return () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(fn, ms);
      };
    }

    function resetCollectionFacet(closeDetails) {
      collectionCheckboxes().forEach((cb) => {
        cb.checked = false;
      });

      syncUrl([]);
      updateCollectionFacetMeta();

      const resetInner = root.querySelector('[data-mcu-facet-reset="collection"]');
      const details =
        resetInner instanceof HTMLElement ? resetInner.closest('details') : null;
      if (closeDetails && details instanceof HTMLDetailsElement) details.open = false;
    }

    root.addEventListener('change', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.matches('[data-mcu-filter-collection]')) {
        syncUrl(selectedCollectionHandles());
        filterItems();
      }
    });

    sortSelect instanceof HTMLSelectElement &&
      sortSelect.addEventListener('change', filterItems);
    searchInput instanceof HTMLInputElement &&
      searchInput.addEventListener('input', debounce(filterItems, 180));

    root.querySelectorAll('[data-mcu-facet-reset]').forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      btn.addEventListener('click', () => {
        if (btn.getAttribute('data-mcu-facet-reset') === 'collection') {
          resetCollectionFacet(true);
          filterItems();
        }
      });
    });

    resetBtn instanceof HTMLButtonElement &&
      resetBtn.addEventListener('click', () => {
        if (searchInput instanceof HTMLInputElement) searchInput.value = '';
        resetCollectionFacet(false);
        if (sortSelect instanceof HTMLSelectElement)
          sortSelect.value = 'featured';

        root.querySelectorAll('details.facets__disclosure').forEach((d) => {
          if (d instanceof HTMLDetailsElement) d.open = false;
        });

        filterItems();
      });

    window.addEventListener('popstate', () => {
      applyCategorySelection(readUrlCategories(), false);
      filterItems();
    });

    filterItems();
  }

  /** @param {HTMLElement} target */
  function mount(target) {
    if (!(target instanceof HTMLElement)) return;
    target.querySelectorAll(ROOT_SEL).forEach((r) => {
      const root = /** @type {HTMLElement} */ (r);
      if (root.dataset.mcuMounted === '1') return;
      root.dataset.mcuMounted = '1';
      initRoot(root);
    });
  }

  mount(document.body);

  ['shopify:section:load', 'shopify:block:select'].forEach((evtName) => {
    document.addEventListener(evtName, () => mount(document.body));
  });
})();
